import { useState, useMemo, useCallback } from 'react'

import { PRODUCT_MODE_REGISTRY, getSupportedVariantsForMode } from '../utils/modeRegistry.js'
import { PRODUCT_VIEW_MODE, PRODUCT_VIEW_MODE_DEFAULT_ORDER } from '../constants/viewModes.js'
import { VIEWER_VARIANT, VIEWER_VARIANT_DEFAULT } from '../constants/viewerVariant.js'

function resolveFromModesList(panelModesConfig) {
  const rawModes = Array.isArray(panelModesConfig?.modes)
    ? panelModesConfig.modes
    : Array.isArray(panelModesConfig)
      ? panelModesConfig
      : []

  if (!rawModes.length) return null

  const knownModes = new Set(PRODUCT_VIEW_MODE_DEFAULT_ORDER)
  const normalizedModes = rawModes
    .map((entry, index) => {
      const id = String(entry?.id ?? '')
      const order = Number(entry?.order)
      return {
        id,
        order: Number.isFinite(order) ? order : index + 1,
        isEnabled: Boolean(entry?.is_enabled),
      }
    })
    .filter((entry) => knownModes.has(entry.id))
    .sort((a, b) => a.order - b.order)

  if (!normalizedModes.length) return null

  const modeOrder = normalizedModes.map((entry) => entry.id)
  const enabledModes = normalizedModes.filter((entry) => entry.isEnabled).map((entry) => entry.id)

  return {
    defaultMode: enabledModes[0] ?? PRODUCT_VIEW_MODE.INFO,
    modeOrder,
    enabledModes,
  }
}

/**
 * useViewerModes — resolves available viewer modes and viewer variant state.
 *
 * Builds the ordered list of modes from:
 *   system registry → panelModesConfig overlay → capabilities guard
 *
 * Viewer variant (2D/3D) is orthogonal to mode. Switching modes may auto-adjust
 * the active variant if the new mode doesn't support the current one.
 *
 * Level 2 hook: manages state + derived values + named actions.
 *
 * @param {object} capabilities — from useProductCapabilities
 * @param {object|null} panelModesConfig — panel settings unit_sheet_modes (or null)
 * @param {string|null} productCategory — for per-category defaults
 * @returns {object}
 */
export function useViewerModes(capabilities, panelModesConfig, productCategory) {
  // ── Resolve initial defaults from panel config ────────────────────────────
  const resolvedDefault = useMemo(() => {
    const fromModesList = resolveFromModesList(panelModesConfig)
    if (fromModesList) return fromModesList

    const byCategory = productCategory ? panelModesConfig?.by_product_category?.[productCategory] : null
    const byScope = byCategory
    const defaults = panelModesConfig?.default ?? null
    return {
      defaultMode: byScope?.default_mode ?? defaults?.default_mode ?? PRODUCT_VIEW_MODE.INFO,
      modeOrder: byScope?.mode_order ?? defaults?.mode_order ?? PRODUCT_VIEW_MODE_DEFAULT_ORDER,
      enabledModes: byScope?.enabled_modes ?? defaults?.enabled_modes ?? PRODUCT_VIEW_MODE_DEFAULT_ORDER,
    }
  }, [panelModesConfig, productCategory])

  // ── Build available modes list ────────────────────────────────────────────
  const availableModes = useMemo(() => {
    return resolvedDefault.modeOrder.flatMap((modeId) => {
      if (!resolvedDefault.enabledModes.includes(modeId)) return []

      const entry = PRODUCT_MODE_REGISTRY.find((e) => e.id === modeId)
      if (!entry) return []
      if (!entry.isSupported(capabilities)) return []

      const availability = entry.buildAvailability(capabilities, panelModesConfig)
      if (!availability.isVisible) return []

      return [
        {
          id: modeId,
          labelKey: entry.labelKey,
          isDisabled: availability.isDisabled,
          disabledReason: availability.disabledReason,
          supportedViewerVariants: entry.supportedViewerVariants,
        },
      ]
    })
  }, [capabilities, resolvedDefault, panelModesConfig])

  // ── Active mode state ─────────────────────────────────────────────────────
  const [activeMode, setActiveModeRaw] = useState(() => {
    const def = resolvedDefault.defaultMode
    const hasDefault = availableModes.some((m) => m.id === def && !m.isDisabled)
    if (hasDefault) return def
    return availableModes.find((m) => !m.isDisabled)?.id ?? PRODUCT_VIEW_MODE.INFO
  })

  // ── Viewer variant state ──────────────────────────────────────────────────
  //
  // Initial variant: if panelSettings says load_with_render_top and the project
  // actually supports 3D, start in 3D. Otherwise default to 2D.
  const [viewerVariant, setViewerVariantRaw] = useState(() =>
    capabilities.preferThreeD && capabilities.allowsThreeDVariant
      ? VIEWER_VARIANT._3D
      : VIEWER_VARIANT_DEFAULT,
  )

  // ── Derived: variants available for the current mode ─────────────────────
  //
  // Intersection of:
  //   - what the mode supports (registry)
  //   - what the project has configured (allowsThreeDVariant from render_top flag)
  //
  // This ensures 3D never appears as an option when no PNG texture was generated.
  const availableViewerVariants = useMemo(() => {
    const modeVariants = getSupportedVariantsForMode(activeMode)
    if (!capabilities.allowsThreeDVariant) {
      return modeVariants.filter((v) => v !== VIEWER_VARIANT._3D)
    }
    return modeVariants
  }, [activeMode, capabilities.allowsThreeDVariant])

  // ── Validate variant vs mode, auto-fallback ───────────────────────────────
  const safeVariant = useMemo(() => {
    if (availableViewerVariants.includes(viewerVariant)) return viewerVariant
    return availableViewerVariants[0] ?? VIEWER_VARIANT_DEFAULT
  }, [viewerVariant, availableViewerVariants])

  // ── Actions ───────────────────────────────────────────────────────────────

  /**
   * setActiveMode — switches to a mode, auto-adjusting viewer variant if needed.
   * Falls back to first non-disabled mode if requested mode is unavailable.
   */
  const setActiveMode = useCallback(
    (modeId) => {
      const target = availableModes.find((m) => m.id === modeId && !m.isDisabled)
      const resolved = target ?? availableModes.find((m) => !m.isDisabled)
      if (!resolved) return

      setActiveModeRaw(resolved.id)

      // If current viewer variant not supported by new mode, fall back
      if (!resolved.supportedViewerVariants.includes(viewerVariant)) {
        setViewerVariantRaw(resolved.supportedViewerVariants[0] ?? VIEWER_VARIANT_DEFAULT)
      }
    },
    [availableModes, viewerVariant],
  )

  /**
   * setViewerVariant — switches viewer variant, ignored if mode doesn't support it.
   */
  const setViewerVariant = useCallback(
    (variant) => {
      if (!availableViewerVariants.includes(variant)) return
      setViewerVariantRaw(variant)
    },
    [availableViewerVariants],
  )

  /**
   * resetActiveMode — returns to resolved default mode and 2D variant.
   */
  const resetActiveMode = useCallback(() => {
    setActiveModeRaw(resolvedDefault.defaultMode)
    setViewerVariantRaw(VIEWER_VARIANT_DEFAULT)
  }, [resolvedDefault.defaultMode])

  return {
    availableModes,
    activeMode,
    defaultMode: resolvedDefault.defaultMode,
    setActiveMode,
    resetActiveMode,
    activeViewerVariant: safeVariant,
    availableViewerVariants,
    setViewerVariant,
  }
}
